import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApi } from '@core/apis/auth.api';
import { LocaleDirection } from '@core/enums';
import { CommonResponse } from '@core/models';
import { AuthPhoneRequest, User } from '@core/models/auth.model';
import { Translations } from '@core/models/translations.model';
import { FirebaseService } from '@core/services/firebase.service';
import { AuthService } from '@shared/services/auth.service';
import { LocaleService } from '@shared/services/locale.service';
import { SnackService } from '@shared/services/snack.service';
import { RecaptchaVerifier } from 'firebase/auth';
import { Observable, firstValueFrom } from 'rxjs';
@Injectable()
export class AuthFacade {
	constructor(
		private readonly authApi: AuthApi,
		private readonly authService: AuthService,
		private readonly firebaseService: FirebaseService,
		private readonly localeService: LocaleService,
		private readonly router: Router,
		private readonly snackService: SnackService
	) {}

	// NOTE: This only for testing. Removed this after the testing phase.
	private readonly testDubaiPhoneNumbers = ['0543175021', '0554867548', '0561595573', '0502462825', '0569026273', '0588665033', '05555555551', '0524036422'];
	private readonly testIndiaPhoneNumbers = ['08054870876', '09611250776'];
	private readonly testIrelandPhoneNumbers = ['0860290404'];
	private readonly testPhoneNumbers = new Map<string, string>([
		['333333330', '0561595573'],
		['333333331', '0554867548'],
		['333333332', '0502462825'],
		['333333333', '0588665033'],
		['333333334', '0569026273'],
		['333333335', '0543175021'],
		['333333338', '08054870876'],
		['333333339', '0860290404'],
		['333333310', '09611250776'],
		['333333340', '0524036422'], // For force error firebase
	]);

	private recaptchaVerifier: RecaptchaVerifier;

	get localeDirection$(): Observable<LocaleDirection> {
		return this.localeService.localeDirection$;
	}
	/**
	 * Handle test phone number
	 *
	 * NOTE: This only for testing. Removed this after the testing phase.
	 */
	getTestPhonePrefix(phone: string): string {
		let phonePrefix = '+964';

		if (this.testDubaiPhoneNumbers.includes(phone)) {
			phonePrefix = '+971';
		} else if (this.testIndiaPhoneNumbers.includes(phone)) {
			phonePrefix = '+91';
		} else if (this.testIrelandPhoneNumbers.includes(phone)) {
			phonePrefix = '+353';
		}

		return phonePrefix;
	}

	/**
	 * Handle test phone number
	 *
	 * NOTE: This only for testing. Removed this after the testing phase.
	 */
	getTestPhoneNumber(phone: string): string {
		if (this.testPhoneNumbers.has(phone)) {
			phone = this.testPhoneNumbers.get(phone);
		} else {
			phone = `07${phone}`;
		}

		return phone;
	}

	getOriginalPhoneNumber(phone: string): string {
		const originalPhone = [...this.testPhoneNumbers].find(([key, value]) => value === phone)?.[0];
		return originalPhone ? `07${originalPhone}` : `${phone}`;
	}

	getTranslations(): Translations {
		return {
			...this.localeService.translations['whats-new']['new-auth'],
			n337: this.localeService.translations['payment']['n337'],
			n174: this.localeService.translations['misc']['n174'],
			n247: this.localeService.translations['user']['user-profile']['n247'],
			n7: this.localeService.translations['header']['n7'],
			n93: this.localeService.translations['logup']['login']['forgot-password']['n93'],
			n270: this.localeService.translations['user']['user-verify']['n270'],
		};
	}

	checkNumberExist(request: AuthPhoneRequest) {
		return this.authApi.checkPhoneRegistered(request);
	}

	/**
	 * Firebase phone authentication
	 */
	// signInWithPhoneNumber(phoneNumber: string): Promise<boolean> {
	// 	return this.firebaseService.signInWithPhoneNumber(phoneNumber, this.recaptchaVerifier);
	// }

	/**
	 * To check if number is valid and to log userData
	 */
	checkNumberIsValid(request: AuthPhoneRequest) {
		return this.authApi.checkNumberIsValid(request);
	}

	/**
	 * Twilio phone authentication
	 */
	signInWithPhoneNumberTwilio(request: AuthPhoneRequest): Promise<CommonResponse> {
		return firstValueFrom(this.authApi.twilioSendOtp(request));
	}

	async login(otpCode: string, request: AuthPhoneRequest): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				// const firebaseAccessToken = await this.firebaseService.sendOtpCode(otpCode);

				// await this.handleLoginAuthentication(request, { firebaseAccessToken });

				//TWILIO CODE
				await this.handleLoginAuthentication(request, { otpCode });

				resolve(true);
			} catch (error) {
				// this.handleLoginAuthentication(request, { otpCode })
				// 	.then(() => resolve(true))
				// 	.catch(() => {
				this.snackService.open(this.getTranslations()['n553'], 5000, null, 'error');

				reject(false);
				// });
			}
		});
	}

	initializeRecaptchaVerifier(): void {
		if (!this.recaptchaVerifier) {
			this.recaptchaVerifier = this.firebaseService.initializeRecaptcha();
		}
	}

	/**
	 * Handle the navigation after login is successfull.
	 *
	 * TODO: This code is from Logup Component. This should be refactored and do a correct way on how navigation should be.
	 */
	loginNavigateTo(navigationData: any): void {
		const user = this.authService.getUser();

		if (
			user['userType'] === '1' &&
			navigationData?.fromPricing === 'pricing' &&
			navigationData?.queryPackage !== undefined &&
			navigationData?.redirect === undefined
		) {
			this.router.navigate(['/payment-dealer'], {
				queryParams: {
					package: navigationData?.queryPackage,
					embedded: navigationData?.embedded,
				},
			});
		} else if (
			user['userType'] === '0' &&
			navigationData?.fromPricing === 'pricing' &&
			navigationData?.queryPackage !== undefined &&
			navigationData?.redirect === undefined
		) {
			this.router.navigate(['/payment'], {
				queryParams: {
					package: navigationData?.queryPackage,
					embedded: navigationData?.embedded,
				},
			});
		} else if (user['userType'] === '0' && navigationData?.isDealer) {
			this.router.navigate(['/dealer/' + user.userID + '/update-profile'], {
				queryParams: {
					redirect: navigationData?.redirect,
					id: navigationData?.assignAd,
					embedded: navigationData?.embedded,
				},
			});
		} else if (navigationData?.redirect !== undefined && navigationData?.assignAd === undefined && navigationData?.queryPackage === undefined) {
			this.router.navigate(['/' + navigationData?.redirect], {
				queryParams: {
					embedded: navigationData?.embedded,
				},
			});
		} else if (navigationData?.fromPlaceAd && navigationData?.assignAd !== undefined && navigationData?.queryPackage === undefined) {
			this.router.navigate(['/payment/tier'], {
				queryParams: {
					from: 'login',
					id: navigationData?.assignAd,
				},
			});
		} else if (navigationData?.assignAd !== undefined) {
			this.router.navigate(['/payment/tier'], {
				queryParams: {
					from: 'login',
					id: navigationData?.assignAd,
				},
			});
		} else if (navigationData?.redirect !== undefined && navigationData?.assignAd !== undefined && navigationData?.queryPackage === undefined) {
			this.router.navigate(['/' + navigationData?.redirect + '/' + navigationData?.assignAd], {
				queryParams: {
					embedded: navigationData?.embedded,
				},
			});
		} else if (navigationData?.redirect !== undefined && navigationData?.assignAd === undefined && navigationData?.queryPackage !== undefined) {
			this.router.navigate(['/' + navigationData?.redirect], {
				queryParams: {
					package: navigationData?.queryPackage,
					embedded: navigationData?.embedded,
				},
			});
		} else if (this.router.url.includes('/login')) {
			this.router.navigate(['/']);
		} else {
			this.router.navigate([navigationData?.redirect === undefined ? '/user/' + user.userID + '/ads' : '/' + navigationData?.redirect], {
				queryParams: {
					from: navigationData?.redirect === 'placead' ? 'login' : undefined,
					embedded: navigationData?.embedded,
				},
			});
		}
	}

	setToggleHeaderLogin(toggleHeaderLogin: boolean): void {
		this.authService.setToggleHeaderLogin(toggleHeaderLogin);
	}

	/**
	 * Verify Twilio OTP and Firebase access token
	 */
	verifyOldMobile(request: AuthPhoneRequest): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				// Check first if the Firebase OTP is correct
				// const firebaseAccessToken = await this.firebaseService.sendOtpCode(request.pin);
				// const { pin: _, ...newRequest } = request;

				// // Verify the Firebase access token in the server side
				// await firstValueFrom(
				// 	this.authApi.verifyOtp({
				// 		...newRequest,
				// 		firebaseAccessToken,
				// 	})
				// );

				// Verify the Twilio OTP code
				await firstValueFrom(
					this.authApi.verifyOtp({
						...request,
					})
				);

				resolve(true);
			} catch (error) {
				// try {
				// 	const { firebaseAccessToken: _, ...newRequest } = request;

				// 	// Verify the Twilio OTP code
				// 	await firstValueFrom(
				// 		this.authApi.verifyOtp({
				// 			...newRequest,
				// 		})
				// 	);

				// 	resolve(true);
				// } catch (error) {
				this.snackService.open(this.getTranslations()['n553'], 5000, null, 'error'); //change to n174 when TWILIO is back

				reject(new Error(error));
				// }
			}
		});
	}

	/**
	 * Delete user's account
	 */
	async deleteAccount(request: AuthPhoneRequest): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				// const firebaseAccessToken = await this.firebaseService.sendOtpCode(request.pin);
				// const { pin: _, ...newRequest } = request;

				// // Verify the Firebase access token in the server side
				// await firstValueFrom(
				// 	this.authApi.verifyOtp({
				// 		...newRequest,
				// 		firebaseAccessToken,
				// 	})
				// );

				// Verify the Twilio OTP code

				await firstValueFrom(
					this.authApi.verifyOtp({
						...request,
					})
				);

				await this.handleDeleteAccountAuthentication(request);

				resolve(true);
			} catch (error) {
				// try {
				// 	const { firebaseAccessToken: _, ...newRequest } = request;

				// 	// Verify the Twilio OTP code
				// 	await firstValueFrom(
				// 		this.authApi.verifyOtp({
				// 			...newRequest,
				// 		})
				// 	);

				// 	await this.handleDeleteAccountAuthentication(request);

				// 	resolve(true);
				// } catch {
				this.snackService.open(this.getTranslations()['n553'], 5000, null, 'error');

				reject(false);
				// }
			}
		});
	}

	/**
	 * Update user's mobile
	 */
	async updateMobile(request: AuthPhoneRequest): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				// const firebaseAccessToken = await this.firebaseService.sendOtpCode(request.pin);

				// await this.handleUpdateMobileAuthentication(request, { firebaseAccessToken });

				//TWILIO CODE
				await this.handleUpdateMobileAuthentication(request, { pin: request.pin });

				resolve(true);
			} catch (error) {
				// this.handleUpdateMobileAuthentication(request, { pin: request.pin })
				// 	.then(() => resolve(true))
				// 	.catch(() => {
				this.snackService.open(this.getTranslations()['n553'], 5000, null, 'error');

				reject(false);
				// });
			}
		});
	}

	/**
	 * Handle delete user's account with Twilio as secondary authentication
	 *
	 * @returns Returns a boolean if authentication is success or not
	 */
	private async handleDeleteAccountAuthentication(request: AuthPhoneRequest): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			firstValueFrom(
				this.authApi.deleteAccount({
					phone: request.phone,
					phonePrefix: request.phonePrefix,
				})
			)
				.then(() => resolve(true))
				.catch((error) => reject(new Error(error.message)));
		});
	}

	/**
	 * Handle login with Twilio as secondary authentication
	 *
	 * @returns Returns a boolean if authentication is success or not
	 */
	private async handleUpdateMobileAuthentication(request: AuthPhoneRequest, token: { firebaseAccessToken?: string; pin?: string }): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			firstValueFrom(
				this.authApi.updateMobile({
					phone: request.phone,
					phonePrefix: request.phonePrefix,
					...(!!token.firebaseAccessToken && { firebaseAccessToken: token.firebaseAccessToken }),
					...(!!token.pin && { pin: token.pin }),
				})
			)
				.then(() => resolve(true))
				.catch((error) => reject(new Error(error.message)));
		});
	}

	/**
	 * Handle login with Twilio as secondary authentication
	 *
	 * @returns An boolean if authentication is success or not
	 */
	private async handleLoginAuthentication(request: AuthPhoneRequest, token: { firebaseAccessToken?: string; otpCode?: string }): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			firstValueFrom(
				this.authApi.login({
					phone: request.phone,
					phonePrefix: request.phonePrefix,
					assignAd: request.assignAd,
					...(!!token.firebaseAccessToken && { firebaseAccessToken: token.firebaseAccessToken }),
					...(!!token.otpCode && { pin: token.otpCode }),
				})
			)
				.then((user: User) => {
					this.authService.loginV2(user);

					resolve(true);
				})
				.catch((error) => {
					reject(new Error(error.message));
				});
		});
	}
}
